import { styled as materialStyled } from "@mui/material/styles";
import { Alert, AlertTitle, Paper, Typography } from "@mui/material";
import {
  type Session as SessionType,
  type SessionLog,
} from "../../utils/types";
import { type Timestamp } from "firebase/firestore";
import React, { useMemo } from "react";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { ErrorOutline, InfoOutlined, TaskAlt } from "@mui/icons-material";
import {
  isAssertionStep,
  isCheckStep,
  parseAssertionConditions,
} from "@/routes/sessions/assertions";

const Info = ({ session, isBaseline }: { session: SessionType; isBaseline?: boolean }) => {
  const parsedStatus = useMemo(() => {
    const status = session.status;
    if (status === "failed") {
      return "Failed";
    } else if (status === "inProgress") {
      return "In progress";
    } else if (status === "auto-completed") {
      return "Auto-completed";
    }
    return "Succeeded";
  }, [session]);

  const titleSeverity = useMemo(() => {
    const status = session.status;
    if (status === "failed") {
      return "error";
    } else if (status === "inProgress") {
      return "info";
    } else if (status === "auto-completed") {
      return "info";
    }
    return "success";
  }, [session]);

  // Session documents are written incrementally by the backend and the SDK, so a doc being read
  // mid-flight -- or one written by an older/newer producer than this client expects -- can be
  // missing fields the types declare as required. Nothing below may assume a field is present.
  const logs = useMemo(
    () => (Array.isArray(session.interactionLogs) ? session.interactionLogs : []),
    [session.interactionLogs]
  );

  const statusMessage = useMemo(() => {
    if (session.status === "inProgress") {
      return undefined;
    }

    const isFailed = session.status === "failed";
    const lastLog = logs.at(-1);

    // A check is excluded even though it is the same record an assert is:
    // its false conditions were handed back to the test, which chose to
    // carry on, so blaming the failure on one names a step that did exactly
    // what it was asked. Whatever stopped the run is further down.
    if (isFailed && lastLog && isAssertionStep(lastLog) && !isCheckStep(lastLog)) {
      const failedConditions = parseAssertionConditions(lastLog).filter(
        (condition) => !condition.result
      );

      if (failedConditions.length > 0) {
        return failedConditions.length === 1
          ? `Assertion failed: ${failedConditions[0].condition}`
          : `Assertions failed: ${failedConditions
              .map((condition) => condition.condition)
              .join("; ")}`;
      }
    }

    const firstGptCommand = lastLog?.gptCommands?.at(0);
    const lastCommand =
      typeof firstGptCommand === "string"
        ? firstGptCommand.toLowerCase()
        : undefined;

    if (lastCommand?.startsWith("error detected")) {
      return lastCommand;
    }

    // The SDK's own reason for stopping the session. This is the only source for failures the
    // model never saw -- a device-side exception, a JUnit timeout, an assertion raised in the
    // test's own code -- where the last log is a perfectly successful step.
    //
    // Absent on every session written before the SDK started sending it, so this must stay a
    // plain optional read: no field access on it, no assumption that it is a string.
    if (isFailed) {
      const reason = session.statusMessage;
      return typeof reason === "string" && reason.trim() ? reason : undefined;
    }

    // `task complete` is a success marker. It is deliberately not reachable for failed sessions:
    // shown under a "Failed" header it read as the model reporting success on a failed run.
    return lastCommand?.startsWith("task complete") ? lastCommand : undefined;
  }, [session, logs]);

  const isLocalStep = (log: SessionLog) =>
    log?.appetizeCommands?.at(0)?.toLowerCase?.() === "user code executed";

  const aiStepsCount = useMemo(() => {
    return logs.reduce((acc, log) => {
      if (log?.fromCache || isLocalStep(log)) {
        return acc;
      }

      return acc + 1;
    }, 0);
  }, [logs]);

  const cachedStepsCount = useMemo(() => {
    return logs.reduce((acc, log) => {
      if (!log?.fromCache) {
        return acc;
      }

      return acc + 1;
    }, 0);
  }, [logs]);

  const localStepsCount = useMemo(() => {
    return logs.reduce((acc, log) => (isLocalStep(log) ? acc + 1 : acc), 0);
  }, [logs]);

  // Firestore Timestamps only arrive once the backend has written them; render a placeholder
  // rather than throwing on `.toMillis()` of a missing or malformed value.
  const formatTimestamp = (value?: Timestamp) => {
    if (typeof value?.toMillis !== "function") {
      return "-";
    }
    const formatted = DateTime.fromMillis(value.toMillis()).toFormat(
      "yyyy-MM-dd hh:mm:ss a"
    );
    return formatted ?? "-";
  };

  return (
    <InfoWrapper variant={"outlined"}>
      <CustomAlert
        hasDescription={!!statusMessage}
        severity={titleSeverity}
        iconMapping={{
          success: <TaskAlt />,
          info:
            session.status === "auto-completed" ? (
              <TaskAlt />
            ) : (
              <InfoOutlined />
            ),
          error: <ErrorOutline />,
        }}
      >
        <AlertTitle>{parsedStatus}{isBaseline && " (Baseline)"}</AlertTitle>
        {statusMessage && statusMessage}
      </CustomAlert>
      <SessionProperties>
        <PropertyGrid>
          <Typography variant={"body2"}>Session ID</Typography>
          <Typography variant={"body2"} sx={{ wordBreak: "break-all" }}>
            {session.id}
          </Typography>
          <Typography variant={"body2"}>Test ID</Typography>
          <Typography variant={"body2"}>
            {!!session.testId ? session.testId : "-"}
          </Typography>
        </PropertyGrid>
        <SectionDivider><span>Device</span></SectionDivider>
        <PropertyGrid>
          <Typography variant={"body2"}>Platform</Typography>
          <Typography variant={"body2"}>
            {session.deviceConfig?.platform || "-"}
          </Typography>
          <Typography variant={"body2"}>Device</Typography>
          <Typography variant={"body2"}>
            {session.deviceConfig?.device || "-"}
          </Typography>
          <Typography variant={"body2"}>OS</Typography>
          <Typography variant={"body2"}>
            {session.deviceConfig?.os || "-"}
          </Typography>
        </PropertyGrid>
        <SectionDivider><span>Timing</span></SectionDivider>
        <PropertyGrid>
          <Typography variant={"body2"}>Started at</Typography>
          <Typography variant={"body2"}>
            {formatTimestamp(session.createdAt)}
          </Typography>
          <Typography variant={"body2"}>Finished at</Typography>
          <Typography variant={"body2"}>
            {formatTimestamp(session.lastUpdatedAt)}
          </Typography>
        </PropertyGrid>
        <SectionDivider><span>Steps</span></SectionDivider>
        <PropertyGrid>
          <Typography variant={"body2"}>Local Steps</Typography>
          <Typography variant={"body2"}>{localStepsCount}</Typography>
          <Typography variant={"body2"}>Cached Steps</Typography>
          <Typography variant={"body2"}>{cachedStepsCount}</Typography>
          <Typography variant={"body2"}>AI Steps</Typography>
          <Typography variant={"body2"}>{aiStepsCount}</Typography>
        </PropertyGrid>
      </SessionProperties>
    </InfoWrapper>
  );
};

const CustomAlert = materialStyled(Alert)(
  ({ hasDescription }: { hasDescription: boolean }) => ({
    ...(!hasDescription && { paddingBottom: "0px" }),
    borderRadius: "3px",
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  })
);

const SessionProperties = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 8px 0;
`;

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 16px;

  span {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.4);
    flex-shrink: 0;
  }

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.12);
  }
`;

const PropertyGrid = styled.div`
  display: grid;
  column-gap: 8px;
  row-gap: 4px;
  grid-template-columns: 1.5fr 2fr;
  padding: 4px 16px 8px;
  align-items: center;
`;

const InfoWrapper = materialStyled(Paper)(() => ({
  gridArea: "info",
  display: "flex",
  flexDirection: "column",
}));

export default Info;
